import React, { useEffect, useState, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Sparkles, Crown, Zap, Building2, ArrowLeft, ArrowRight, CreditCard, Shield, Clock, Star, Phone, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useLanguage } from '../hooks/useLanguage';
import { useCountry } from '../hooks/useCountry';
import { useAuth } from '../hooks/useAuth';
import { paymobAPI } from '../utils/paymob';
import { stripeAPI } from '../utils/stripe';
import { toast } from '../components/ui/Toast';
type BillingPeriod = 'monthly' | 'yearly';
interface PricingPlan {
  id: string;
  name: string;
  nameAr: string;
  icon: React.ElementType;
  color: string;
  features: string[];
  featuresAr: string[];
  popular?: boolean;
}
export function SubscriptionPage() {
  const {
    language,
    t
  } = useLanguage();
  const {
    country,
    setCountry,
    currency,
    currencySymbol,
    pricing,
    whatsappNumber,
    phoneNumber
  } = useCountry();
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const isRTL = language === 'ar';
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [isProcessingPayment, setIsProcessingPayment] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // Set country based on user's registered phone number
  useEffect(() => {
    if (user?.country) {
      setCountry(user.country as 'eg' | 'ae' | 'sa');
    }
  }, [user, setCountry]);
  const plans: PricingPlan[] = [{
    id: 'starter',
    name: 'Starter',
    nameAr: 'المبتدئ',
    icon: Zap,
    color: 'from-blue-500 to-cyan-500',
    features: ['Up to 5,000 Monthly Conversations', 'Limited AI Replies', 'Dashboard', 'Conversations Inbox', 'Limited Follow-up', 'Products & Services', 'Limited Offers Management', 'Limited AI Auto Replies', 'Basic Analytics & Reports', 'Standard Support'],
    featuresAr: ['حتى 5,000 محادثة شهرياً', 'ردود AI محدودة', 'لوحة التحكم', 'صندوق المحادثات', 'متابعة محدودة', 'المنتجات والخدمات', 'إدارة عروض محدودة', 'ردود تلقائية AI محدودة', 'تحليلات وتقارير أساسية', 'دعم قياسي']
  }, {
    id: 'business',
    name: 'Growth',
    nameAr: 'النمو',
    icon: Crown,
    color: 'from-purple-500 to-pink-500',
    features: ['Up to 10,000 Monthly Conversations', 'Standard AI Replies', 'Dashboard', 'Conversations Inbox', 'Full Leads Management', 'Follow-up (Reminders & Auto)', 'Products & Services', 'Offers Management', 'AI Auto Replies', 'Standard Analytics & Reports', 'Priority Support'],
    featuresAr: ['حتى 10,000 محادثة شهرياً', 'ردود AI قياسية', 'لوحة التحكم', 'صندوق المحادثات', 'إدارة عملاء كاملة', 'متابعة (تذكيرات وتلقائية)', 'المنتجات والخدمات', 'إدارة العروض', 'ردود تلقائية AI', 'تحليلات وتقارير قياسية', 'دعم ذو أولوية'],
    popular: true
  }, {
    id: 'pro',
    name: 'Scale',
    nameAr: 'التوسع',
    icon: Star,
    color: 'from-orange-500 to-red-500',
    features: ['Up to 100,000 Monthly Conversations', 'High AI Replies', 'Dashboard', 'Conversations Inbox', 'Advanced Leads Management', 'Follow-up (Reminders & Auto)', 'Products & Services', 'Offers Management', 'AI Auto Replies', 'Advanced Analytics & Reports', 'Priority Support'],
    featuresAr: ['حتى 100,000 محادثة شهرياً', 'ردود AI عالية', 'لوحة التحكم', 'صندوق المحادثات', 'إدارة عملاء متقدمة', 'متابعة (تذكيرات وتلقائية)', 'المنتجات والخدمات', 'إدارة العروض', 'ردود تلقائية AI', 'تحليلات وتقارير متقدمة', 'دعم ذو أولوية']
  }];
  const handleSubscribe = async (planId: string) => {
    setPaymentError(null);
    // ── All countries → Stripe ────────────────────────────────────────
    setIsProcessingPayment(planId);
    try {
      stripeAPI.redirectToCheckout({
        planId,
        billingPeriod,
        country: country || 'global',
        email: user?.email,
        name: user?.name,
        phone: user?.phone
      });
    } catch (error) {
      console.error('Stripe payment error:', error);
      setPaymentError(isRTL ? 'حدث خطأ أثناء بدء عملية الدفع. يرجى المحاولة مرة أخرى.' : 'Error initiating payment. Please try again.');
      setTimeout(() => setPaymentError(null), 5000);
    } finally {
      setIsProcessingPayment(null);
    }
  };
  const getPrice = (planId: string) => {
    const planPricing = pricing[planId as keyof typeof pricing];
    if (planPricing && typeof planPricing === 'object' && 'monthly' in planPricing) {
      const p = planPricing as {
        monthly: number;
        yearly: number;
      };
      return billingPeriod === 'monthly' ? p.monthly : p.yearly;
    }
    return 0;
  };
  const getCountryName = (code: string) => {
    const names: Record<string, {
      ar: string;
      en: string;
    }> = {
      eg: {
        ar: 'مصر',
        en: 'Egypt'
      },
      ae: {
        ar: 'الإمارات',
        en: 'UAE'
      },
      sa: {
        ar: 'السعودية',
        en: 'Saudi Arabia'
      }
    };
    return isRTL ? names[code]?.ar : names[code]?.en;
  };
  const getCountryFlag = (code: string) => {
    const flags: Record<string, string> = {
      eg: '🇪🇬',
      ae: '🇦🇪',
      sa: '🇸🇦'
    };
    return flags[code] || '🌍';
  };
  const getCurrencyLabel = (code: string) => {
    const labels: Record<string, string> = {
      eg: 'ج.م',
      ae: 'درهم',
      sa: 'ر.س'
    };
    return labels[code] || '';
  };
  return <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-purple-50" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-3046">
      {/* Header - Simplified without currency selector */}
      <motion.header initial={{
      y: -100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200" data-id="element-3047">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3048">
          <div className="flex items-center justify-between py-3 sm:py-4" data-id="element-3049">
            <Link to="/dashboard" className="flex items-center gap-2" data-id="element-3050">
              {isRTL ? <ArrowRight className="w-5 h-5 text-gray-600" data-id="element-3051" /> : <ArrowLeft className="w-5 h-5 text-gray-600" data-id="element-3052" />}
              <span className="text-gray-600 font-medium text-sm sm:text-base" data-id="element-3053">
                {t('subscription.backToDashboard')}
              </span>
            </Link>

            {/* Show current country/currency info */}
            {user?.country && <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-lg border border-purple-200" data-id="element-3054">
                <span className="text-lg" data-id="element-3055">{getCountryFlag(user.country)}</span>
                <span className="text-sm font-medium text-purple-700" data-id="element-3056">
                  {getCurrencyLabel(user.country)}
                </span>
              </div>}
          </div>
        </div>
      </motion.header>

      {/* User Country Notice */}
      {user?.country && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-purple-50 border-b border-purple-100" data-id="element-3057">
          <div className="max-w-7xl mx-auto px-4 py-2 text-center" data-id="element-3058">
            <span className="text-sm text-purple-700" data-id="element-3059">
              {getCountryFlag(user.country)}{' '}
              {isRTL ? `أنت مسجل من ${getCountryName(user.country)} - الأسعار معروضة بـ ${getCurrencyLabel(user.country)}` : `You're registered from ${getCountryName(user.country)} - Prices shown in ${getCurrencyLabel(user.country)}`}
            </span>
          </div>
        </motion.div>}

      {/* Payment Error Banner */}
      {paymentError && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -10
    }} className="bg-red-50 border-b border-red-200" data-id="element-3060">
          <div className="max-w-7xl mx-auto px-4 py-3 text-center" data-id="element-3061">
            <span className="text-sm text-red-700 font-medium" data-id="element-3062">
              ⚠️ {paymentError}
            </span>
          </div>
        </motion.div>}

      {/* Hero Section */}
      <section className="pt-8 sm:pt-16 pb-6 sm:pb-8 relative overflow-hidden" data-id="element-3063">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-3064">
          <motion.div animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0]
        }} transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-purple-100/50 to-pink-100/50 rounded-full blur-3xl" data-id="element-3065" />
          <motion.div animate={{
          scale: [1.2, 1, 1.2],
          rotate: [0, -90, 0]
        }} transition={{
          duration: 25,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-blue-100/50 to-green-100/50 rounded-full blur-3xl" data-id="element-3066" />
        </div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-3067">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4" data-id="element-3068">
            {t('subscription.title')}
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8" data-id="element-3069">
            {t('subscription.subtitle')}
          </motion.p>

          {/* Billing Toggle */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="inline-flex items-center gap-2 sm:gap-4 bg-white rounded-xl sm:rounded-2xl p-1.5 sm:p-2 shadow-lg border border-gray-200" data-id="element-3070">
            <button onClick={() => setBillingPeriod('monthly')} className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all ${billingPeriod === 'monthly' ? 'bg-[#4f1964] text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3071">
              {t('pricing.monthly')}
            </button>
            <button onClick={() => setBillingPeriod('yearly')} className={`relative px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all ${billingPeriod === 'yearly' ? 'bg-[#4f1964] text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3072">
              {t('pricing.yearly')}
              <span className="absolute -top-2 -right-2 px-1.5 sm:px-2 py-0.5 bg-green-500 text-white text-[10px] sm:text-xs font-bold rounded-full" data-id="element-3073">
                {t('pricing.save')}
              </span>
            </button>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-6 sm:py-12 px-4" data-id="element-3074">
        <div className="max-w-6xl mx-auto" data-id="element-3075">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8" data-id="element-3076">
            {plans.map((plan, index) => <motion.div key={plan.id} initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1 * index
          }} whileHover={{
            y: -5,
            scale: 1.01
          }} className={`relative bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-xl border-2 transition-all ${plan.popular ? 'border-[#4f1964] shadow-purple-200' : 'border-gray-100 hover:border-purple-200'}`} data-id="element-3077">
                {/* Popular Badge */}
                {plan.popular && <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.5,
              type: 'spring'
            }} className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 px-3 sm:px-4 py-1 sm:py-1.5 bg-gradient-to-r from-[#4f1964] to-purple-600 text-white rounded-full text-xs sm:text-sm font-bold shadow-lg whitespace-nowrap" data-id="element-3078">
                    ⭐ {t('pricing.popular')}
                  </motion.div>}

                {/* Icon */}
                <motion.div whileHover={{
              rotate: [0, -10, 10, 0]
            }} transition={{
              duration: 0.5
            }} className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4 sm:mb-6 shadow-lg`} data-id="element-3079">
                  <plan.icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" data-id="element-3080" />
                </motion.div>

                {/* Plan Name */}
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2" data-id="element-3081">
                  {isRTL ? plan.nameAr : plan.name}
                </h3>

                {/* Price */}
                <div className="mb-4 sm:mb-6" data-id="element-3082">
                  <div className="flex items-center gap-2 mb-1" data-id="element-3083">
                    <span className="text-sm sm:text-base text-gray-400 line-through" data-id="element-3084">
                      {(getPrice(plan.id) * 2).toLocaleString()}{' '}
                      {currencySymbol}
                    </span>
                    <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full" data-id="element-3085">
                      -50%
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1 sm:gap-2" data-id="element-3086">
                    <motion.span key={`${country}-${billingPeriod}`} initial={{
                  opacity: 0,
                  scale: 0.5
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} className="text-2xl sm:text-4xl font-bold text-gray-900" data-id="element-3087">
                      {getPrice(plan.id).toLocaleString()}
                    </motion.span>
                    <span className="text-gray-500 text-sm sm:text-base" data-id="element-3088">
                      {currencySymbol}
                    </span>
                    <span className="text-gray-400 text-xs sm:text-sm" data-id="element-3089">
                      /{' '}
                      {billingPeriod === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear')}
                    </span>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-2 sm:space-y-4 mb-6 sm:mb-8" data-id="element-3090">
                  {(isRTL ? plan.featuresAr : plan.features).map((feature, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.3 + i * 0.1
              }} className="flex items-center gap-2 sm:gap-3" data-id="element-3091">
                        <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" data-id="element-3092" />
                        <span className="text-gray-700 text-sm sm:text-base" data-id="element-3093">
                          {feature}
                        </span>
                      </motion.li>)}
                </ul>

                {/* CTA Button */}
                <motion.button whileHover={{
              scale: 1.03
            }} whileTap={{
              scale: 0.98
            }} onClick={() => handleSubscribe(plan.id)} disabled={isProcessingPayment === plan.id} className={`w-full py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-lg transition-all flex items-center justify-center gap-2 ${plan.popular ? 'bg-gradient-to-r from-[#4f1964] to-purple-600 text-white shadow-lg shadow-purple-300' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`} data-id="element-3094">
                  {isProcessingPayment === plan.id ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-3095" />
                      {isRTL ? 'جاري التحويل...' : 'Processing...'}
                    </> : <>
                      <CreditCard className="w-5 h-5" data-id="element-3096" />
                      {isRTL ? 'ادفع الآن' : 'Pay Now'}
                    </>}
                </motion.button>

                {/* Payment Provider Badge - show for all countries */}
                <div className="mt-3 flex items-center justify-center gap-2 opacity-70" data-id="element-3097">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-3098">
                    {isRTL ? 'مدعوم بواسطة' : 'Powered by'}
                  </span>
                  <span className="text-xs font-bold text-[#635bff]" data-id="element-3099">
                    Stripe
                  </span>
                  <div className="flex gap-1" data-id="element-3100">
                    <div className="h-3 w-5 bg-blue-100 rounded-sm text-[8px] flex items-center justify-center font-bold text-blue-700" data-id="element-3101">
                      V
                    </div>
                    <div className="h-3 w-5 bg-red-100 rounded-sm text-[8px] flex items-center justify-center font-bold text-red-700" data-id="element-3102">
                      M
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>

          {/* Custom Plan */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="mt-8 sm:mt-12 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl sm:rounded-3xl p-5 sm:p-8 border-2 border-dashed border-purple-300" data-id="element-3103">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6" data-id="element-3104">
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-right" data-id="element-3105">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-lg" data-id="element-3106">
                  <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" data-id="element-3107" />
                </div>
                <div data-id="element-3108">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-3109">
                    {t('pricing.custom')}
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base" data-id="element-3110">
                    {t('pricing.customDesc')}
                  </p>
                </div>
              </div>
              <motion.a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.98
            }} className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#4f1964] to-purple-600 text-white rounded-xl font-bold shadow-lg text-center text-sm sm:text-base" data-id="element-3111">
                {t('pricing.contactUs')}
              </motion.a>
            </div>
          </motion.div>

          {/* ── Feature Comparison Table ──────────────────────────────────── */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="mt-12 sm:mt-16" data-id="element-3112">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8" data-id="element-3113">
              {isRTL ? 'مقارنة الخطط' : 'Included Features by Plan'}
            </h2>

            <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-lg bg-white" data-id="element-3114">
              <table className="w-full text-sm sm:text-base" data-id="element-3115">
                <thead data-id="element-3116">
                  <tr className="bg-[#1e293b] text-white" data-id="element-3117">
                    <th className="text-start px-4 sm:px-6 py-3 sm:py-4 font-bold min-w-[200px]" data-id="element-3118">
                      {isRTL ? 'الميزة' : 'Feature'}
                    </th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 font-bold text-center" data-id="element-3119">
                      {isRTL ? 'المبتدئ' : 'Starter'}
                    </th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 font-bold text-center" data-id="element-3120">
                      <span className="inline-flex flex-col items-center gap-0.5" data-id="element-3121">
                        {isRTL ? 'النمو' : 'Growth'}
                        <span className="text-[10px] sm:text-xs font-normal text-purple-200" data-id="element-3122">
                          ({isRTL ? 'الأكثر شعبية' : 'Most Popular'})
                        </span>
                      </span>
                    </th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 font-bold text-center" data-id="element-3123">
                      {isRTL ? 'التوسع' : 'Scale'}
                    </th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 font-bold text-center" data-id="element-3124">
                      {isRTL ? 'المؤسسات' : 'Enterprise'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-3125">
                  {[{
                  feature: isRTL ? 'المحادثات الشهرية' : 'Monthly Conversations',
                  starter: isRTL ? 'حتى 5,000' : 'Up to 5,000',
                  growth: isRTL ? 'حتى 10,000' : 'Up to 10,000',
                  scale: isRTL ? 'حتى 100,000' : 'Up to 100,000',
                  enterprise: isRTL ? 'مخصص' : 'Custom'
                }, {
                  feature: isRTL ? 'ردود الذكاء الاصطناعي' : 'AI Replies',
                  starter: isRTL ? 'محدود' : 'Limited',
                  growth: isRTL ? 'قياسي' : 'Standard',
                  scale: isRTL ? 'عالي' : 'High',
                  enterprise: isRTL ? 'مخصص' : 'Custom'
                }, {
                  feature: isRTL ? 'لوحة التحكم' : 'Dashboard',
                  starter: isRTL ? 'نعم' : 'Yes',
                  growth: isRTL ? 'نعم' : 'Yes',
                  scale: isRTL ? 'نعم' : 'Yes',
                  enterprise: isRTL ? 'نعم' : 'Yes'
                }, {
                  feature: isRTL ? 'صندوق المحادثات' : 'Conversations Inbox',
                  starter: isRTL ? 'نعم' : 'Yes',
                  growth: isRTL ? 'نعم' : 'Yes',
                  scale: isRTL ? 'نعم' : 'Yes',
                  enterprise: isRTL ? 'نعم' : 'Yes'
                }, {
                  feature: isRTL ? 'إدارة العملاء المحتملين' : 'Leads Management',
                  starter: isRTL ? 'لا' : 'No',
                  growth: isRTL ? 'كامل' : 'Full',
                  scale: isRTL ? 'متقدم' : 'Advanced',
                  enterprise: isRTL ? 'مخصص' : 'Custom'
                }, {
                  feature: isRTL ? 'المتابعة (تذكيرات ومتابعة تلقائية)' : 'Follow-up (Reminders & Auto Follow-ups)',
                  starter: isRTL ? 'محدود' : 'Limited',
                  growth: isRTL ? 'نعم' : 'Yes',
                  scale: isRTL ? 'نعم' : 'Yes',
                  enterprise: isRTL ? 'نعم' : 'Yes'
                }, {
                  feature: isRTL ? 'المنتجات والخدمات' : 'Products & Services',
                  starter: isRTL ? 'نعم' : 'Yes',
                  growth: isRTL ? 'نعم' : 'Yes',
                  scale: isRTL ? 'نعم' : 'Yes',
                  enterprise: isRTL ? 'نعم' : 'Yes'
                }, {
                  feature: isRTL ? 'إدارة العروض' : 'Offers Management',
                  starter: isRTL ? 'محدود' : 'Limited',
                  growth: isRTL ? 'نعم' : 'Yes',
                  scale: isRTL ? 'نعم' : 'Yes',
                  enterprise: isRTL ? 'نعم' : 'Yes'
                }, {
                  feature: isRTL ? 'الردود التلقائية بالذكاء الاصطناعي' : 'AI Auto Replies',
                  starter: isRTL ? 'محدود' : 'Limited',
                  growth: isRTL ? 'نعم' : 'Yes',
                  scale: isRTL ? 'نعم' : 'Yes',
                  enterprise: isRTL ? 'نعم' : 'Yes'
                }, {
                  feature: isRTL ? 'التحليلات والتقارير' : 'Analytics & Reports',
                  starter: isRTL ? 'أساسي' : 'Basic',
                  growth: isRTL ? 'قياسي' : 'Standard',
                  scale: isRTL ? 'متقدم' : 'Advanced',
                  enterprise: isRTL ? 'مخصص' : 'Custom'
                }, {
                  feature: isRTL ? 'الدعم الفني' : 'Support',
                  starter: isRTL ? 'قياسي' : 'Standard',
                  growth: isRTL ? 'أولوية' : 'Priority',
                  scale: isRTL ? 'أولوية' : 'Priority',
                  enterprise: isRTL ? 'مخصص' : 'Dedicated'
                }].map((row, i) => <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-id="element-3126">
                      <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-gray-900" data-id="element-3127">
                        {row.feature}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600" data-id="element-3128">
                        <span className={row.starter === 'No' || row.starter === 'لا' ? 'text-red-500 font-medium' : row.starter === 'Yes' || row.starter === 'نعم' ? 'text-green-600 font-medium' : ''} data-id="element-3129">
                          {row.starter}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 bg-purple-50/30" data-id="element-3130">
                        <span className={row.growth === 'Yes' || row.growth === 'نعم' ? 'text-green-600 font-medium' : ''} data-id="element-3131">
                          {row.growth}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600" data-id="element-3132">
                        <span className={row.scale === 'Yes' || row.scale === 'نعم' ? 'text-green-600 font-medium' : ''} data-id="element-3133">
                          {row.scale}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600" data-id="element-3134">
                        {row.enterprise}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </motion.div>
          {/* ─────────────────────────────────────────────────────────────── */}
        </div>
      </section>

      {/* Contact Info Based on Country */}
      <section className="py-6 sm:py-8 px-4 bg-gray-50" data-id="element-3135">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3136">
          <p className="text-gray-600 mb-4" data-id="element-3137">
            {isRTL ? 'للتواصل والاستفسارات:' : 'For inquiries and support:'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4" data-id="element-3138">
            <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors" data-id="element-3139">
              <Phone className="w-4 h-4" data-id="element-3140" />
              <span data-id="element-3141">{phoneNumber}</span>
            </a>
            <a href="mailto:hello@operines.ai" className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors" data-id="element-3142">
              <span data-id="element-3143">hello@operines.ai</span>
            </a>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 sm:py-12 px-4" data-id="element-3144">
        <div className="max-w-4xl mx-auto" data-id="element-3145">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6" data-id="element-3146">
            {[{
            icon: Shield,
            label: t('subscription.securePayment')
          }, {
            icon: Clock,
            label: t('subscription.cancelAnytime')
          }, {
            icon: CreditCard,
            label: t('subscription.multiplePayment')
          }, {
            icon: Sparkles,
            label: t('subscription.support247')
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6 + i * 0.1
          }} className="flex flex-col items-center gap-2 p-3 sm:p-4 bg-white rounded-xl shadow-sm" data-id="element-3147">
                <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#4f1964]" data-id="element-3148" />
                <span className="text-xs sm:text-sm text-gray-600 text-center" data-id="element-3149">
                  {item.label}
                </span>
              </motion.div>)}
          </div>
        </div>
      </section>
    </div>;
}